/**
 * The Tabbed pages section, with sticky tabs on desktop.
 *
 * Each tab is a grid item of its own, on a row of its own, and a sticky item only sticks within
 * its grid area, so a tab on its row can't move at all. Sticky tabs instead all span the whole
 * tab column and are stacked in it by their margins: each one's top margin is the height of the
 * tabs above it, its bottom margin the height of those below. Every tab then occupies the full
 * column, so they stick together and scroll away together at the end of the section.
 *
 * CSS can't know the tabs' heights, so this measures them. Until it has, the tabs keep their rows
 * and don't stick.
 */
class TabbedPages extends HTMLElement {
  #resizeObserver = new ResizeObserver(() => this.#stack());

  connectedCallback() {
    if (!this.classList.contains("tabbed-pages--sticky")) return;

    // Observing fires once straight away, which does the first measurement.
    for (const tab of this.#tabs()) this.#resizeObserver.observe(tab);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  #tabs() {
    return Array.from(
      this.querySelectorAll(
        ":scope > .tabbed-pages__item > .tabbed-pages__tab",
      ),
    );
  }

  #stack() {
    const tabs = this.#tabs();
    const gap =
      parseFloat(
        getComputedStyle(this).getPropertyValue("--tabbed-pages-tab-gap"),
      ) || 0;
    const heights = tabs.map((tab) => tab.getBoundingClientRect().height);
    const total =
      heights.reduce((sum, height) => sum + height, 0) +
      gap * Math.max(tabs.length - 1, 0);

    let before = 0;
    tabs.forEach((tab, index) => {
      const height = heights[index] ?? 0;
      if (!(tab instanceof HTMLElement)) return;

      tab.style.setProperty("--tabbed-pages-tab-before", `${before}px`);
      tab.style.setProperty(
        "--tabbed-pages-tab-after",
        `${total - before - height}px`,
      );
      before += height + gap;
    });

    this.setAttribute("data-stacked", "");
  }
}

if (!customElements.get("tabbed-pages")) {
  customElements.define("tabbed-pages", TabbedPages);
}
